import { useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Icon } from '@mysaiva/core'
import { getBackToSaivaPath } from '@mysaiva/core/developer-platform'
import LogoSmall from '../../../assets/logo-small.svg?react'
import {
  WORKSPACE_PAGES,
  WORKSPACE_SECTIONS,
  activeSidebarItemId,
  resolveWorkspaceLocation,
  subPageLabel,
  subPagePath,
  workspacePath,
  type WorkspacePage
} from '../navigation'
import styles from './WorkspaceSidebar.module.scss'

interface WorkspaceSidebarProps {
  /** Organisation card rendered under the brand block. */
  organization?: ReactNode
  /** Account menu rendered at the bottom, after Support. */
  account?: ReactNode
  /**
   * Show the "Back to SAIVA" link. Off for Developer-role users: every
   * product sends them straight back to the workspace (SAIV-11643).
   */
  showBackToSaiva?: boolean
}

const SUPPORT_URL = '/zendesk/redirect'

/**
 * Developer Platform sidebar (design review SAIV-11493). Unlike the product
 * apps' sidebar it is always dark navy, never collapses, carries no icons
 * apart from Support (page entries are text-only), expands documentation
 * sub-pages in place. Everything is read from the page registry so the
 * breadcrumb, search and routes can never disagree with it. Webhooks and
 * SFTP Exports are intentionally not listed (SAIV-11351 / SAIV-11352).
 */
export function WorkspaceSidebar({
  organization,
  account,
  showBackToSaiva = true
}: WorkspaceSidebarProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()

  const current = resolveWorkspaceLocation(location.pathname)
  const activeId = activeSidebarItemId(current)
  const activeParentId = current.subPage ? current.page.id : null

  // The open documentation group follows the route; the user can still
  // fold it, and it re-opens on the next navigation into it. The pathname
  // is a dependency on purpose: moving between two pages of the same
  // folded group (e.g. via search) must reopen it too.
  const [openId, setOpenId] = useState<string | null>(activeParentId)
  useEffect(() => {
    setOpenId(activeParentId)
  }, [activeParentId, location.pathname])

  const sections = useMemo(
    () =>
      WORKSPACE_SECTIONS.map(section => ({
        ...section,
        pages: WORKSPACE_PAGES.filter(page => page.section === section.id)
      })),
    []
  )

  const go = (path: string) => {
    if (path !== location.pathname) navigate(path)
  }

  const renderPage = (page: WorkspacePage) => {
    const children = page.children ?? []
    const isParent = children.length > 0
    const isOpen = isParent && openId === page.id
    const isActive = activeId === page.id
    const hasActiveChild = activeParentId === page.id

    const onClick = () => {
      if (!isParent) {
        go(workspacePath(page))
        return
      }
      if (isOpen) {
        setOpenId(null)
        return
      }
      setOpenId(page.id)
      // Opening a group lands the user on its first page (SAIV-9439),
      // unless they are already inside it.
      if (!hasActiveChild) go(subPagePath(page, children[0]))
    }

    return (
      <div key={page.id} className={styles.group}>
        <button
          type="button"
          className={clsx(styles.item, {
            [styles.itemActive]: isActive,
            [styles.itemMuted]: isParent && !hasActiveChild
          })}
          onClick={onClick}
          aria-current={isActive ? 'page' : undefined}
          aria-expanded={isParent ? isOpen : undefined}
          data-testid={`dp-nav-${page.id}`}
        >
          {page.icon && (
            <span className={styles.itemIcon} aria-hidden="true">
              <Icon icon={page.icon} size={16} />
            </span>
          )}
          <span className={styles.itemLabel}>{t(page.labelKey)}</span>
          {isParent && (
            <span className={clsx(styles.chevron, isOpen && styles.chevronOpen)} aria-hidden="true">
              <Icon icon="ChevronSmallDown" size={20} />
            </span>
          )}
        </button>
        {isOpen && (
          <div className={styles.children} role="group" aria-label={t(page.labelKey)}>
            {children.map(child => {
              const childActive = activeId === child.id
              return (
                <button
                  key={child.id}
                  type="button"
                  className={clsx(styles.item, styles.child, childActive && styles.itemActive)}
                  onClick={() => go(subPagePath(page, child))}
                  aria-current={childActive ? 'page' : undefined}
                  data-testid={`dp-nav-${child.id}`}
                >
                  <span className={styles.itemLabel}>{subPageLabel(child, t)}</span>
                </button>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  return (
    <nav
      className={styles.sidebar}
      aria-label={t('developerPlatform.workspace')}
      data-testid="dp-sidebar"
    >
      <div className={styles.inner}>
        <Link to={workspacePath('dashboard')} className={styles.brand} data-testid="dp-brand">
          <span className={styles.brandMark} aria-hidden="true">
            <LogoSmall width={15} height={15} />
          </span>
          <span className={styles.brandName}>SAIVA</span>
          <span className={styles.brandProduct}>Developers</span>
        </Link>
        {showBackToSaiva && (
          <a className={styles.backLink} href={getBackToSaivaPath()} data-testid="dp-back-to-saiva">
            <Icon icon="ArrowSmallLeft" size={16} />
            {t('developerPlatform.backToSaiva')}
          </a>
        )}
        <div className={styles.divider} />
        {organization}

        <div className={styles.nav}>
          <div className={styles.sections}>
            {sections.map(section => (
              <div
                key={section.id}
                className={clsx(
                  styles.section,
                  section.id === 'documentation' && styles.sectionDocs
                )}
                data-testid={`dp-nav-section-${section.id}`}
              >
                <div className={styles.sectionTitle}>{t(section.titleKey)}</div>
                {section.pages.map(renderPage)}
              </div>
            ))}
          </div>

          <div className={styles.bottom}>
            <button
              type="button"
              className={clsx(styles.item, styles.itemMuted)}
              onClick={() => window.open(SUPPORT_URL, '_blank', 'noopener,noreferrer')}
              data-testid="dp-nav-support"
            >
              <span className={styles.itemIcon} aria-hidden="true">
                <Icon icon="QuestionCircle" size={16} />
              </span>
              <span className={styles.itemLabel}>{t('common.support')}</span>
            </button>
            {account && <div className={styles.account}>{account}</div>}
            <div className={styles.bottomDivider} />
          </div>
        </div>
      </div>
    </nav>
  )
}
